import type BigNumber from 'bignumber.js';
import { FundListSortColumn, SortDirection } from '@/types/fund.types';
import type { ActivityModel, ActivityModelInternal } from '@/types/model.types';
import { toActivityModel } from './money';

/**
 * Lifted out of DataService with the behaviour preserved. These are pure
 * functions now, which means they are directly unit-testable and can be
 * memoized by the caller instead of re-running on every render.
 *
 * The ticker, name and amount-range filters that used to live here are gone:
 * they served a sort-and-filter dialog on the Angular home page that was
 * never wired into its template, so no user could reach them.
 */

function compareStrings(a: string, b: string, direction: SortDirection): number {
  const result = a > b ? 1 : a < b ? -1 : 0;
  return direction === SortDirection.ASCENDING ? result : -result;
}

function compareAmounts(
  a: BigNumber,
  b: BigNumber,
  tieBreakerA: string,
  tieBreakerB: string,
  direction: SortDirection,
): number {
  if (a.isEqualTo(b)) {
    return compareStrings(tieBreakerA, tieBreakerB, direction);
  }
  const result = a.isGreaterThan(b) ? 1 : -1;
  return direction === SortDirection.ASCENDING ? result : -result;
}

export function getCustomerActivitiesSortFunction(
  sortColumn: FundListSortColumn,
  sortDirection: SortDirection,
): (a: ActivityModel, b: ActivityModel) => number {
  switch (sortColumn) {
    case FundListSortColumn.TICKER:
      return (a, b) => compareStrings(a.fund!.ticker, b.fund!.ticker, sortDirection);
    case FundListSortColumn.FUND_NAME:
      return (a, b) => compareStrings(a.fund!.name, b.fund!.name, sortDirection);
    case FundListSortColumn.LARGEST_TRADE:
      return (a, b) =>
        compareAmounts(
          a.largestTradeAmount,
          b.largestTradeAmount,
          a.fund!.ticker,
          b.fund!.ticker,
          sortDirection,
        );
    case FundListSortColumn.ACTIVITY:
    default:
      return (a, b) =>
        compareAmounts(
          a.aggregateAmount,
          b.aggregateAmount,
          a.fund!.ticker,
          b.fund!.ticker,
          sortDirection,
        );
  }
}

/**
 * Widens money to BigNumber and sorts. Activities with no fund are dropped:
 * that isn't a user filter but a guard, since every column the list shows
 * (and every sort) reads from the fund.
 */
export function sortCustomerActivities(
  sortColumn: FundListSortColumn,
  sortDirection: SortDirection,
  rawActivities: ActivityModelInternal[],
): ActivityModel[] {
  const activities = rawActivities
    .map(toActivityModel)
    .filter((activity) => activity.fund !== undefined);

  activities.sort(getCustomerActivitiesSortFunction(sortColumn, sortDirection));
  return activities;
}
